import React from "react";
import { Phone, BadgeCheck, Zap, ShieldCheck, DollarSign, Clock, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { COMPANY, TRUST_BADGES } from "../mock";
import GoogleGuaranteeBadge from "./GoogleGuaranteeBadge";
import { trackPhoneClick } from "../lib/analytics";

const iconMap = { BadgeCheck, Zap, ShieldCheck, DollarSign };

const Hero = ({ onBookClick }) => {
  return (
    <section
      id="home"
      className="relative bg-slate-900 text-white overflow-hidden"
    >
      {/* Background: solid gradient (the former 15%-opacity photo was the LCP element and added ~2.4s on mobile) */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900/95 to-slate-800" />

      {/* Subtle dot pattern */}
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)",
          backgroundSize: "28px 28px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 pt-14 pb-20 lg:pt-20 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* LEFT - Copy */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-orange-500/15 border border-orange-500/40 text-orange-300 text-[11px] font-bold uppercase tracking-widest mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-orange-400 animate-pulse" />
              Trusted Appliance Repair Near Me in Decatur GA
            </div>

            <h1
              className="text-[40px] sm:text-5xl lg:text-[58px] font-extrabold leading-[1.04] tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800 }}
              data-testid="hero-h1"
            >
              Expert Home Appliance Repair
              <br />
              <span className="text-orange-500">in Decatur & Metro Atlanta</span>
            </h1>

            <p className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed">
              Top-Rated mobile appliance repair near you in Decatur, Atlanta, Stone Mountain, Lithonia, Tucker & Conyers. Expert Samsung, LG, and GE appliance repair — professional refrigerator, washer, dryer, and oven repair by factory-trained, local smart appliance specialists.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/contact#book">
                <Button
                                    size="lg"
                  data-testid="hero-book-button"
                  className="bg-orange-700 hover:bg-orange-800 text-white font-bold uppercase tracking-wider text-sm h-12 px-7 shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 transition-shadow"
                >
                  Book a Repair Online
                </Button>
              </Link>
              <a href={`tel:${COMPANY.phoneRaw}`} onClick={() => trackPhoneClick("hero")} data-testid="hero-call-now-link">
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/5 border-white/30 text-white hover:bg-white/15 hover:text-white font-bold uppercase tracking-wider text-sm h-12 px-7 backdrop-blur w-full sm:w-auto"
                >
                  Call Now
                </Button>
              </a>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {TRUST_BADGES.map((b) => {
                const Icon = iconMap[b.icon];
                return (
                  <div
                    key={b.label}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-white/5 border border-white/10"
                  >
                    <Icon className="h-4 w-4 text-green-400 flex-shrink-0" />
                    <span className="text-[12.5px] font-semibold text-slate-200">{b.label}</span>
                  </div>
                );
              })}
            </div>

            {/* Google Guarantee Badge */}
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <GoogleGuaranteeBadge variant="full" />
              <div className="text-sm text-slate-300 max-w-xs leading-snug">
                Verified by Google Local Services — backed by Google's customer guarantee.
              </div>
            </div>
          </div>

          {/* RIGHT - Quick Contact Card */}
          <div className="lg:col-span-5">
            <div className="bg-white text-slate-900 rounded-2xl shadow-2xl p-7 lg:p-8">
              <div className="text-xs uppercase tracking-widest text-orange-700 font-bold mb-1">
                Quick Contact
              </div>
              <div
                className="text-2xl font-extrabold mb-6"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800 }}
              >
                Get In Touch Right Now
              </div>

              <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-orange-50 transition-colors border border-slate-100">
                <div className="h-11 w-11 rounded-xl bg-orange-500 flex items-center justify-center flex-shrink-0">
                  <Phone className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Call Us</div>
                  <div className="text-xl font-bold text-slate-900">{COMPANY.phone}</div>
                </div>
              </a>

              <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100 mt-3">
                <div className="h-11 w-11 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
                  <Clock className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Business Hours</div>
                  <div className="text-[15px] font-bold text-slate-900">Mon–Sat: 8AM – 6PM</div>
                </div>
              </div>

              <Link to="/contact#book">
                <Button
                                    size="lg"
                  data-testid="hero-online-booking-button"
                  className="w-full mt-5 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wider text-sm h-12"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  24/7 Online Booking
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
